import { CompilerInvariantError } from "../../../errors";
import { requireDefined } from "../../../utils/presence";
import type {
  AggregateExpr,
  ComposableQuery,
  FulltextMatchPredicate,
  NodePredicate,
  QueryAst,
  SetOperation,
  VectorSimilarityPredicate,
} from "../../ast";
import {
  getDialect,
  type SqlDialect,
  type VectorStrategy,
} from "../../dialect";
import {
  runFulltextPredicatePass,
  runRecursiveTraversalSelectionPass,
  runVectorPredicatePass,
  type VariableLengthTraversal,
} from "../passes";
import type {
  AggregatePlanNode,
  LimitOffsetPlanNode,
  LogicalPlan,
  LogicalPlanNode,
  ProjectPlanNode,
  ResultFilterPlanNode,
} from "./types";

export type LowerStandardQueryToLogicalPlanInput = Readonly<{
  ast: QueryAst;
  collapsedTraversalCteAlias?: string;
  dialect: SqlDialect;
  effectiveLimit?: number;
  fulltextPredicate?: FulltextMatchPredicate;
  graphId: string;
  vectorPredicate?: VectorSimilarityPredicate;
}>;

export type LowerRecursiveQueryToLogicalPlanInput = Readonly<{
  ast: QueryAst;
  dialect: SqlDialect;
  graphId: string;
  traversal?: VariableLengthTraversal;
}>;

export type LowerSetOperationToLogicalPlanInput = Readonly<{
  dialect: SqlDialect;
  graphId: string;
  op: SetOperation;
  /**
   * The backend-configured vector strategy, threaded so a set-operation leaf's
   * vector predicate validates against the same metric set as standalone leaf
   * compilation (rather than only the dialect's fallback metric list).
   */
  vectorStrategy?: VectorStrategy;
}>;

/**
 * Creates a sequential plan node ID generator scoped to a single compilation invocation.
 * IDs are NOT stable across compilations — different compilations of the same query
 * may produce different IDs depending on pass execution order.
 */
function createPlanNodeIdFactory(): () => string {
  let current = 0;
  return function nextPlanNodeId(): string {
    current += 1;
    return `plan_${current.toString(36)}`;
  };
}

function extractAggregateExpressions(ast: QueryAst): readonly AggregateExpr[] {
  const aggregates: AggregateExpr[] = [];

  for (const field of ast.projection.fields) {
    if ("__type" in field.source && field.source.__type === "aggregate") {
      aggregates.push(field.source);
    }
  }

  return aggregates;
}

function getAliasPredicates(
  ast: QueryAst,
  alias: string,
  predicateTargetType: "edge" | "node",
): readonly NodePredicate[] {
  return ast.predicates.filter((predicate) => {
    const targetType = predicate.targetType ?? "node";
    return (
      predicate.targetAlias === alias && targetType === predicateTargetType
    );
  });
}

function wrapWithAliasFilterNode(
  currentNode: LogicalPlanNode,
  ast: QueryAst,
  alias: string,
  predicateTargetType: "edge" | "node",
  nextPlanNodeId: () => string,
): LogicalPlanNode {
  const aliasPredicates = getAliasPredicates(ast, alias, predicateTargetType);
  if (aliasPredicates.length === 0) {
    return currentNode;
  }

  return {
    alias,
    id: nextPlanNodeId(),
    input: currentNode,
    op: "filter",
    predicateTargetType,
    predicates: aliasPredicates.map((predicate) => predicate.expression),
  };
}

function appendAggregateSortLimitAndProjectNodes(
  currentNode: LogicalPlanNode,
  ast: QueryAst,
  nextPlanNodeId: () => string,
  limit: number | undefined,
  collapsedTraversalCteAlias?: string,
): LogicalPlanNode {
  let node: LogicalPlanNode =
    ast.resultPredicate === undefined ?
      currentNode
    : ({
        id: nextPlanNodeId(),
        input: currentNode,
        op: "result_filter",
        predicate: ast.resultPredicate,
      } satisfies ResultFilterPlanNode);

  const aggregateExpressions = extractAggregateExpressions(ast);
  if (
    aggregateExpressions.length > 0 ||
    ast.groupBy !== undefined ||
    ast.having !== undefined
  ) {
    const aggregateNode: AggregatePlanNode = {
      aggregates: aggregateExpressions,
      groupBy: ast.groupBy?.fields ?? [],
      id: nextPlanNodeId(),
      input: node,
      op: "aggregate",
    };
    node =
      ast.having === undefined ?
        aggregateNode
      : { ...aggregateNode, having: ast.having };
  }

  const hasFieldOrderBy = ast.orderBy !== undefined && ast.orderBy.length > 0;
  const hasAggregateOrderBy =
    ast.aggregateOrderBy !== undefined && ast.aggregateOrderBy.length > 0;
  if (hasFieldOrderBy || hasAggregateOrderBy) {
    // The sort node's presence (not its `orderBy` payload) is what the
    // emitter's plan-shape invariant reads — see `hasSort` in
    // emitter/plan-inspector.ts. `orderBy` here can be `[]` when only
    // `ast.aggregateOrderBy` is set; that's fine today since nothing reads
    // this field's contents, only whether the node exists.
    node = {
      id: nextPlanNodeId(),
      input: node,
      op: "sort",
      orderBy: ast.orderBy ?? [],
    };
  }

  if (limit !== undefined || ast.offset !== undefined) {
    const limitOffsetNodeBase: Omit<LimitOffsetPlanNode, "limit" | "offset"> = {
      id: nextPlanNodeId(),
      input: node,
      op: "limit_offset",
    };
    const hasLimit = limit !== undefined;
    const hasOffset = ast.offset !== undefined;

    if (hasLimit && hasOffset) {
      node = {
        ...limitOffsetNodeBase,
        limit,
        offset: ast.offset,
      };
    } else if (hasLimit) {
      node = { ...limitOffsetNodeBase, limit };
    } else if (hasOffset) {
      node = { ...limitOffsetNodeBase, offset: ast.offset };
    } else {
      throw new CompilerInvariantError(
        "limit_offset node requires limit or offset to be present",
      );
    }
  }

  const projectNodeBase: Omit<ProjectPlanNode, "collapsedTraversalAlias"> = {
    fields: ast.projection.fields,
    id: nextPlanNodeId(),
    input: node,
    op: "project",
  };
  return collapsedTraversalCteAlias === undefined ? projectNodeBase : (
      {
        ...projectNodeBase,
        collapsedTraversalAlias: collapsedTraversalCteAlias,
      }
    );
}

type LowerStandardQueryToLogicalPlanNodeInput =
  LowerStandardQueryToLogicalPlanInput &
    Readonly<{
      nextPlanNodeId: () => string;
    }>;

function lowerStandardQueryToLogicalPlanNode(
  input: LowerStandardQueryToLogicalPlanNodeInput,
): LogicalPlanNode {
  const { ast, nextPlanNodeId } = input;

  let currentNode: LogicalPlanNode = {
    alias: ast.start.alias,
    graphId: input.graphId,
    id: nextPlanNodeId(),
    kinds: ast.start.kinds,
    op: "scan",
    source: "nodes",
  };

  currentNode = wrapWithAliasFilterNode(
    currentNode,
    ast,
    ast.start.alias,
    "node",
    nextPlanNodeId,
  );

  for (const traversal of ast.traversals) {
    currentNode = {
      direction: traversal.direction,
      edgeAlias: traversal.edgeAlias,
      edgeKinds: traversal.edgeKinds,
      id: nextPlanNodeId(),
      input: currentNode,
      inverseEdgeKinds: traversal.inverseEdgeKinds ?? [],
      joinFromAlias: traversal.joinFromAlias,
      joinType: traversal.optional ? "left" : "inner",
      nodeAlias: traversal.nodeAlias,
      nodeKinds: traversal.nodeKinds,
      op: "join",
    };

    currentNode = wrapWithAliasFilterNode(
      currentNode,
      ast,
      traversal.edgeAlias,
      "edge",
      nextPlanNodeId,
    );
    currentNode = wrapWithAliasFilterNode(
      currentNode,
      ast,
      traversal.nodeAlias,
      "node",
      nextPlanNodeId,
    );
  }

  if (input.vectorPredicate !== undefined) {
    currentNode = {
      id: nextPlanNodeId(),
      input: currentNode,
      op: "vector_knn",
      predicate: input.vectorPredicate,
    };
  }

  if (input.fulltextPredicate !== undefined) {
    currentNode = {
      id: nextPlanNodeId(),
      input: currentNode,
      op: "fulltext_match",
      predicate: input.fulltextPredicate,
    };
  }

  return appendAggregateSortLimitAndProjectNodes(
    currentNode,
    ast,
    nextPlanNodeId,
    input.effectiveLimit,
    input.collapsedTraversalCteAlias,
  );
}

type LowerRecursiveQueryToLogicalPlanNodeInput =
  LowerRecursiveQueryToLogicalPlanInput &
    Readonly<{
      nextPlanNodeId: () => string;
    }>;

function lowerRecursiveQueryToLogicalPlanNode(
  input: LowerRecursiveQueryToLogicalPlanNodeInput,
): LogicalPlanNode {
  const { ast, nextPlanNodeId } = input;
  const traversal =
    input.traversal ?? runRecursiveTraversalSelectionPass(input.ast);

  let currentNode: LogicalPlanNode = {
    alias: ast.start.alias,
    graphId: input.graphId,
    id: nextPlanNodeId(),
    kinds: ast.start.kinds,
    op: "scan",
    source: "nodes",
  };

  currentNode = wrapWithAliasFilterNode(
    currentNode,
    ast,
    ast.start.alias,
    "node",
    nextPlanNodeId,
  );

  currentNode = {
    edgeAlias: traversal.edgeAlias,
    edgeKinds: traversal.edgeKinds,
    id: nextPlanNodeId(),
    input: currentNode,
    inverseEdgeKinds: traversal.inverseEdgeKinds ?? [],
    nodeAlias: traversal.nodeAlias,
    nodeKinds: traversal.nodeKinds,
    op: "recursive_expand",
    traversal: traversal.variableLength,
  };

  currentNode = wrapWithAliasFilterNode(
    currentNode,
    ast,
    traversal.edgeAlias,
    "edge",
    nextPlanNodeId,
  );
  currentNode = wrapWithAliasFilterNode(
    currentNode,
    ast,
    traversal.nodeAlias,
    "node",
    nextPlanNodeId,
  );

  return appendAggregateSortLimitAndProjectNodes(
    currentNode,
    ast,
    nextPlanNodeId,
    ast.limit,
  );
}

function lowerComposableQueryToLogicalPlanNode(
  query: ComposableQuery,
  dialect: SqlDialect,
  graphId: string,
  nextPlanNodeId: () => string,
  vectorStrategy: VectorStrategy | undefined,
): LogicalPlanNode {
  if ("__type" in query) {
    return lowerSetOperationToLogicalPlanNode(
      query,
      graphId,
      dialect,
      nextPlanNodeId,
      vectorStrategy,
    );
  }

  const hasVariableLengthTraversal = query.traversals.some(
    (traversal) => traversal.variableLength !== undefined,
  );
  if (hasVariableLengthTraversal) {
    return lowerRecursiveQueryToLogicalPlanNode({
      ast: query,
      dialect,
      graphId,
      nextPlanNodeId,
    });
  }

  const dialectAdapter = getDialect(dialect);
  const vectorPredicate = runVectorPredicatePass(
    query,
    dialectAdapter,
    vectorStrategy,
  ).vectorPredicate;
  const fulltextPredicate = runFulltextPredicatePass(
    query,
    dialectAdapter,
  ).fulltextPredicate;
  const effectiveLimit = query.limit;
  const loweringInput = {
    ast: query,
    dialect,
    graphId,
    nextPlanNodeId,
    ...(effectiveLimit === undefined ? {} : { effectiveLimit }),
    ...(vectorPredicate === undefined ? {} : { vectorPredicate }),
    ...(fulltextPredicate === undefined ? {} : { fulltextPredicate }),
  };
  return lowerStandardQueryToLogicalPlanNode(loweringInput);
}

function lowerSetOperationToLogicalPlanNode(
  op: SetOperation,
  graphId: string,
  dialect: SqlDialect,
  nextPlanNodeId: () => string,
  vectorStrategy: VectorStrategy | undefined,
): LogicalPlanNode {
  let currentNode: LogicalPlanNode = {
    id: nextPlanNodeId(),
    left: lowerComposableQueryToLogicalPlanNode(
      op.left,
      dialect,
      graphId,
      nextPlanNodeId,
      vectorStrategy,
    ),
    op: "set_op",
    operator: op.operator,
    right: lowerComposableQueryToLogicalPlanNode(
      op.right,
      dialect,
      graphId,
      nextPlanNodeId,
      vectorStrategy,
    ),
  };

  if (op.orderBy !== undefined && op.orderBy.length > 0) {
    currentNode = {
      id: nextPlanNodeId(),
      input: currentNode,
      op: "sort",
      orderBy: op.orderBy,
    };
  }

  if (op.limit !== undefined || op.offset !== undefined) {
    const limitOffsetBase = {
      id: nextPlanNodeId(),
      input: currentNode,
      op: "limit_offset" as const,
    };
    if (op.limit !== undefined && op.offset !== undefined) {
      currentNode = { ...limitOffsetBase, limit: op.limit, offset: op.offset };
    } else if (op.limit === undefined) {
      currentNode = { ...limitOffsetBase, offset: requireDefined(op.offset) };
    } else {
      currentNode = { ...limitOffsetBase, limit: op.limit };
    }
  }

  return currentNode;
}

export function lowerStandardQueryToLogicalPlan(
  input: LowerStandardQueryToLogicalPlanInput,
): LogicalPlan {
  const nextPlanNodeId = createPlanNodeIdFactory();
  return {
    metadata: {
      dialect: input.dialect,
      graphId: input.graphId,
    },
    root: lowerStandardQueryToLogicalPlanNode({
      ...input,
      nextPlanNodeId,
    }),
  };
}

export function lowerRecursiveQueryToLogicalPlan(
  input: LowerRecursiveQueryToLogicalPlanInput,
): LogicalPlan {
  const nextPlanNodeId = createPlanNodeIdFactory();
  return {
    metadata: {
      dialect: input.dialect,
      graphId: input.graphId,
    },
    root: lowerRecursiveQueryToLogicalPlanNode({
      ...input,
      nextPlanNodeId,
    }),
  };
}

export function lowerSetOperationToLogicalPlan(
  input: LowerSetOperationToLogicalPlanInput,
): LogicalPlan {
  const nextPlanNodeId = createPlanNodeIdFactory();
  return {
    metadata: {
      dialect: input.dialect,
      graphId: input.graphId,
    },
    root: lowerSetOperationToLogicalPlanNode(
      input.op,
      input.graphId,
      input.dialect,
      nextPlanNodeId,
      input.vectorStrategy,
    ),
  };
}
